import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminAuthService from '../services/adminAuthService';
import { updateUser } from '../services/adminUserService';
import { logActivity } from '../services/adminActivityService';
import { getRoleDisplayName } from '../utils/adminPermissions';
import './SettingsPage.css';

const SettingsPage = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState({
    fname: '',
    lname: '',
    email: '',
    phoneNumber: '',
  });
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [authUser, userRow] = await Promise.all([
        AdminAuthService.getCurrentUser(),
        AdminAuthService.getUserRow(),
      ]);
      setProfile({
        fname: userRow.fname || '',
        lname: userRow.lname || '',
        email: authUser.email || userRow.email || '',
        phoneNumber: userRow.phoneNumber || userRow.phone || '',
      });
      setRole(await AdminAuthService.getAdminRole());
    } catch (err) {
      setError(err.message || t('settings.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleProfileChange = (field, value) => {
    setProfile((p) => ({ ...p, [field]: value }));
    clearMessages();
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setError(null);
    setSuccess(null);
    try {
      const userId = await AdminAuthService.getCurrentUserId();
      await updateUser(userId, {
        fname: profile.fname?.trim() || '',
        lname: profile.lname?.trim() || '',
        phoneNumber: profile.phoneNumber?.trim() || '',
      });
      AdminAuthService.clearUserCache();
      await logActivity({
        action: 'update',
        resourceType: 'admin_user',
        resourceId: userId,
        details: 'Updated own profile',
      });
      setSuccess(t('settings.profileUpdated'));
    } catch (err) {
      setError(err.message || t('settings.profileUpdateFailed'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm((p) => ({ ...p, [field]: value }));
    clearMessages();
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword?.trim()) {
      setError(t('settings.enterCurrentPassword'));
      return;
    }
    if (!newPassword?.trim()) {
      setError(t('settings.enterNewPassword'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('settings.passwordTooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('settings.passwordsDontMatch'));
      return;
    }
    setSavingPassword(true);
    setError(null);
    setSuccess(null);
    try {
      await AdminAuthService.updatePassword(currentPassword, newPassword);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess(t('settings.passwordUpdated'));
    } catch (err) {
      setError(AdminAuthService.getErrorMessage(err) || err.message);
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="dashboard-spinner" />
        <p>{t('settings.loading')}</p>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1 className="dashboard-title">{t('settings.accountSettings')}</h1>
        <p className="dashboard-subtitle">
          {t('settings.subtitle')}
        </p>
      </div>

      {(error || success) && (
        <div
          className={`settings-message ${error ? 'settings-error' : 'settings-success'}`}
          role="alert"
        >
          {error || success}
          <button type="button" onClick={clearMessages} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="settings-sections">
        <section className="settings-section">
          <h2 className="settings-section-title">{t('settings.profile')}</h2>
          <form onSubmit={handleSaveProfile} className="settings-form">
            <div className="settings-form-grid">
              <div className="settings-field">
                <label htmlFor="settings-fname">{t('settings.firstName')}</label>
                <input
                  id="settings-fname"
                  type="text"
                  value={profile.fname}
                  onChange={(e) => handleProfileChange('fname', e.target.value)}
                  className="settings-input"
                  placeholder="Jane"
                />
              </div>
              <div className="settings-field">
                <label htmlFor="settings-lname">{t('settings.lastName')}</label>
                <input
                  id="settings-lname"
                  type="text"
                  value={profile.lname}
                  onChange={(e) => handleProfileChange('lname', e.target.value)}
                  className="settings-input"
                  placeholder="Doe"
                />
              </div>
              <div className="settings-field settings-field-full">
                <label htmlFor="settings-email">{t('settings.email')}</label>
                <input
                  id="settings-email"
                  type="email"
                  value={profile.email}
                  readOnly
                  className="settings-input settings-input-readonly"
                  title={t('settings.emailReadonlyTitle')}
                />
                <span className="settings-hint">{t('settings.emailReadonlyHint')}</span>
              </div>
              <div className="settings-field settings-field-full">
                <label htmlFor="settings-phone">{t('settings.phone')}</label>
                <input
                  id="settings-phone"
                  type="tel"
                  value={profile.phoneNumber}
                  onChange={(e) => handleProfileChange('phoneNumber', e.target.value)}
                  className="settings-input"
                  placeholder="+255 123 456 789"
                />
              </div>
              <div className="settings-field settings-field-full">
                <label>{t('settings.role')}</label>
                <span className="settings-role-badge">{getRoleDisplayName(role)}</span>
              </div>
            </div>
            <div className="settings-actions">
              <button
                type="submit"
                className="settings-btn-primary"
                disabled={savingProfile}
              >
                {savingProfile ? t('settings.savingEllipsis') : t('settings.saveProfile')}
              </button>
            </div>
          </form>
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">{t('settings.changePassword')}</h2>
          <form onSubmit={handleUpdatePassword} className="settings-form">
            <div className="settings-form-grid">
              <div className="settings-field settings-field-full">
                <label htmlFor="settings-current-password">{t('settings.currentPassword')}</label>
                <input
                  id="settings-current-password"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  className="settings-input"
                  placeholder={t('settings.enterCurrentPasswordPlaceholder')}
                  autoComplete="current-password"
                />
              </div>
              <div className="settings-field settings-field-full">
                <label htmlFor="settings-new-password">{t('settings.newPassword')}</label>
                <input
                  id="settings-new-password"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  className="settings-input"
                  placeholder={t('settings.min8Chars')}
                  autoComplete="new-password"
                />
              </div>
              <div className="settings-field settings-field-full">
                <label htmlFor="settings-confirm-password">{t('settings.confirmNewPassword')}</label>
                <input
                  id="settings-confirm-password"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  className="settings-input"
                  placeholder={t('settings.reenterNewPassword')}
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="settings-actions">
              <button
                type="submit"
                className="settings-btn-primary"
                disabled={savingPassword || !passwordForm.currentPassword || !passwordForm.newPassword}
              >
                {savingPassword ? t('settings.updatingEllipsis') : t('settings.updatePassword')}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default SettingsPage;
